import { type FormEvent, useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router';

import {
  type Guideline,
  GUIDELINES_PAGE,
  type GuidelineStatus,
  SCOPE_LABELS,
  scopeHasCondition,
  SCOPES,
  STATUS_LABELS,
  STATUSES,
} from '@/shared/triage/guidelines';
import {
  CATEGORIES,
  CATEGORY_LABELS,
  DESTINATION_LABELS,
  DESTINATIONS,
  type EmailCategory,
  type TriageDestination,
} from '@/shared/triage/vocabulary';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Dialog } from '@/shared/ui/Dialog';
import { EmptyState } from '@/shared/ui/EmptyState';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { Input, Select } from '@/shared/ui/Input';
import { Segmented } from '@/shared/ui/Segmented';
import { Spinner } from '@/shared/ui/Spinner';
import { useIsPlatform } from '@/shared/session/currentUser';
import { formatDate } from '@/shared/utils/format';
import { useDebounced } from '@/shared/utils/useDebounced';

import {
  appliesToText,
  effectText,
  guidelineChanges,
  guidelineEditForm,
  type GuidelineEditForm,
  inOrder,
  outcomeText,
} from './guidelineEdit';
import { PROFILE_LABELS } from './types';
import { useDeleteGuideline, useGuidelines, useUpdateGuideline } from './useAdmin';

const dayFormat = new Intl.DateTimeFormat('es-ES', { dateStyle: 'short' });

export const NOTHING_LEARNED_TITLE = 'Todavía no ha aprendido nada';
export const NOTHING_LEARNED_HINT = 'Aprende de las notas que se escriben al corregir un correo.';

/**
 * Administración → «Lo que ha aprendido» (contract v10.35, ADR 0030 of the back).
 *
 * Every criterion the platform distilled from a note somebody wrote when correcting an
 * email: what it says, how far it reaches (the sender, the sender under a condition, any
 * sender), which kinds of email, what it changes, the note it came from with who wrote it
 * and that email, and how it has done since (applied, confirmed, corrected; more corrected
 * than confirmed is a criterion to look at). Turned off, it stops acting from the next
 * email on; «Editar» changes what the distillation got wrong.
 *
 * For whoever administers, not only Ignira: Víctor and Ernesto are who wrote the notes,
 * and the API shows each the criteria of their own mailboxes' rules. Only a superadministrator
 * sees «Borrar» (contract v10.36), for a request to erase someone's data, and the search box
 * is how they find what names that person.
 */
export function LearnedTab() {
  const [status, setStatus] = useState<GuidelineStatus>('vigente');
  const [offset, setOffset] = useState(0);
  const [editing, setEditing] = useState<Guideline | null>(null);
  const [deleting, setDeleting] = useState<Guideline | null>(null);
  const [typed, setTyped] = useState('');
  const search = useDebounced(typed).trim();
  const platform = useIsPlatform();
  const guidelines = useGuidelines({ status_filter: status, q: search, offset });
  const remove = useDeleteGuideline(() => setDeleting(null));

  const data = guidelines.data;
  const counts = data?.counts ?? {};
  const everything = STATUSES.reduce((sum, each) => sum + (counts[each] ?? 0), 0);
  const total = data?.total ?? 0;
  const profiles = new Set(data?.items.map((item) => item.profile));

  return (
    <div className="flex flex-col gap-4">
      <p className="text-ink-2 text-[14px] leading-relaxed">
        Lo que la plataforma ha sacado de las notas que se escriben al corregir un correo, con la
        nota de la que salió. Se aplica desde el siguiente correo; lo ya clasificado no cambia. Si
        un criterio no es lo que se quiso decir, edítalo o desactívalo.
      </p>

      {guidelines.isPending ? <Spinner variant="block" label="Cargando lo aprendido" /> : null}
      {guidelines.isError ? (
        <ErrorMessage error={guidelines.error}>No se pudo cargar lo aprendido</ErrorMessage>
      ) : null}

      {data && everything === 0 && !search ? (
        <EmptyState title={NOTHING_LEARNED_TITLE} hint={NOTHING_LEARNED_HINT} />
      ) : null}

      {data && (everything > 0 || search) ? (
        <>
          <Input
            label="Buscar"
            type="search"
            value={typed}
            maxLength={200}
            placeholder="Remitente, nombre o palabras de la nota"
            onChange={(event) => {
              setTyped(event.target.value);
              setOffset(0);
            }}
          />
          <Segmented
            label="Estado de los criterios"
            value={status}
            options={STATUSES.map((each) => ({
              value: each,
              label: STATUS_LABELS[each],
              count: counts[each] ?? 0,
            }))}
            onChange={(next) => {
              setStatus(next);
              setOffset(0);
            }}
          />
          {data.items.length === 0 ? (
            <p className="text-ink-3 text-[14px]">
              {search
                ? `Ninguno en «${STATUS_LABELS[status].toLowerCase()}» con «${search}».`
                : `Ninguno en «${STATUS_LABELS[status].toLowerCase()}».`}
            </p>
          ) : (
            <ul aria-label="Criterios aprendidos" className="flex flex-col gap-3">
              {data.items.map((guideline) => (
                <GuidelineItem
                  key={guideline.id}
                  guideline={guideline}
                  showProfile={profiles.size > 1}
                  onEdit={() => setEditing(guideline)}
                  onDelete={
                    platform
                      ? () => {
                          remove.reset();
                          setDeleting(guideline);
                        }
                      : undefined
                  }
                />
              ))}
            </ul>
          )}
          {total > GUIDELINES_PAGE ? (
            <div className="flex items-center justify-between gap-3 text-[13px]">
              <span className="text-ink-3">
                {offset + 1}–{Math.min(offset + GUIDELINES_PAGE, total)} de {total}
              </span>
              <span className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - GUIDELINES_PAGE))}
                >
                  Anteriores
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={offset + GUIDELINES_PAGE >= total}
                  onClick={() => setOffset(offset + GUIDELINES_PAGE)}
                >
                  Siguientes
                </Button>
              </span>
            </div>
          ) : null}
        </>
      ) : null}

      {editing ? (
        <EditGuidelineDialog guideline={editing} onClose={() => setEditing(null)} />
      ) : null}
      {deleting ? (
        <DeleteGuidelineDialog
          guideline={deleting}
          busy={remove.isPending}
          error={remove.isError ? remove.error : null}
          onConfirm={() => remove.mutate(deleting.id)}
          onCancel={() => setDeleting(null)}
        />
      ) : null}
    </div>
  );
}

/**
 * «Borrar» for good (contract v10.36). Says what goes and what stays, because the note
 * staying on its correction is the part nobody would guess.
 */
function DeleteGuidelineDialog({
  guideline,
  busy,
  error,
  onConfirm,
  onCancel,
}: {
  guideline: Guideline;
  busy: boolean;
  error: unknown;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <Dialog
      title="¿Borrar este criterio para siempre?"
      size="sm"
      onClose={busy ? () => undefined : onCancel}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={onConfirm} disabled={busy}>
            {busy ? 'Borrando…' : 'Borrar para siempre'}
          </Button>
        </>
      }
    >
      <div className="text-ink-2 flex flex-col gap-2 text-[14.5px] leading-relaxed">
        <p>
          <strong className="text-ink">«{guideline.text}»</strong> deja de aplicarse y desaparece de
          esta lista y del «por qué» de los correos en los que se aplicó. No se puede deshacer.
        </p>
        <p className="text-ink-3 text-[13.5px]">
          Para cuando alguien pide que se borren sus datos. La nota se queda en su corrección y no
          se vuelve a aprender de ella. Si solo no es lo que se quiso decir, mejor desactivarlo.
        </p>
      </div>
      {error ? <ErrorMessage error={error} /> : null}
    </Dialog>
  );
}

function GuidelineItem({
  guideline,
  showProfile,
  onEdit,
  onDelete,
}: {
  guideline: Guideline;
  showProfile: boolean;
  onEdit: () => void;
  /** Only for a superadministrator. */
  onDelete?: (() => void) | undefined;
}) {
  const update = useUpdateGuideline();
  const outcome = outcomeText(guideline);
  const inForce = guideline.status === 'vigente';
  const statusLine = statusText(guideline);

  return (
    <li
      aria-label={guideline.text}
      className="bg-card border-line flex flex-col gap-3 rounded-lg border px-4 py-3"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="text-ink text-[15px] leading-snug font-medium">{guideline.text}</p>
        <Badge>{SCOPE_LABELS[guideline.scope]}</Badge>
      </div>

      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-[13px]">
        {guideline.sender_key ? (
          <>
            <dt className="text-ink-3">Remitente</dt>
            <dd className="text-ink-2 break-all">{guideline.sender_key}</dd>
          </>
        ) : null}
        <dt className="text-ink-3">Tipo de correo</dt>
        <dd className="text-ink-2">{appliesToText(guideline.applies_to)}</dd>
        {guideline.condition ? (
          <>
            <dt className="text-ink-3">Condición</dt>
            <dd className="text-ink-2">{guideline.condition}</dd>
          </>
        ) : null}
        <dt className="text-ink-3">Efecto</dt>
        <dd className="text-ink-2">{effectText(guideline)}</dd>
        {showProfile ? (
          <>
            <dt className="text-ink-3">Reglas</dt>
            <dd className="text-ink-2">{PROFILE_LABELS[guideline.profile]}</dd>
          </>
        ) : null}
      </dl>

      <div className="text-ink-2 flex flex-col gap-1 text-[13px]">
        <p>
          Nota de {guideline.author ?? 'alguien'} ({formatDate(dayFormat, guideline.created_at)}
          ): <span className="italic">«{guideline.note}»</span>
        </p>
        {guideline.source_email_id ? (
          <p>
            <Link to={`/correos/${guideline.source_email_id}`} className="link">
              {guideline.source_email_subject || 'Ver el correo'}
            </Link>
          </p>
        ) : null}
        {guideline.remarks ? <p className="text-ink-3 italic">{guideline.remarks}</p> : null}
        {statusLine ? <p className="text-ink-3">{statusLine}</p> : null}
      </div>

      <p
        className={
          outcome.worrying ? 'text-warn text-[13px] font-medium' : 'text-ink-3 text-[13px]'
        }
      >
        {outcome.text}
        {outcome.worrying ? '. Revísalo: se corrige más de lo que se confirma.' : null}
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          disabled={update.isPending}
          onClick={() =>
            update.mutate({
              id: guideline.id,
              change: { status: inForce ? 'desactivado' : 'vigente' },
            })
          }
        >
          {inForce ? 'Desactivar' : 'Volver a activar'}
        </Button>
        <Button variant="secondary" size="sm" disabled={update.isPending} onClick={onEdit}>
          Editar
        </Button>
        {onDelete ? (
          <Button variant="ghost" size="sm" disabled={update.isPending} onClick={onDelete}>
            Borrar
          </Button>
        ) : null}
      </div>
      {update.isError ? <ErrorMessage error={update.error}>No se pudo cambiar</ErrorMessage> : null}
    </li>
  );
}

/** Why it is not in force, and who touched it by hand, if anybody did. */
function statusText(guideline: Guideline): string | null {
  const parts: string[] = [];
  if (guideline.status !== 'vigente') {
    const reason =
      guideline.status_reason ??
      (guideline.status === 'sustituido'
        ? 'Lo sustituyó una nota posterior.'
        : 'Está desactivado.');
    const when = guideline.status_changed_at
      ? ` (${formatDate(dayFormat, guideline.status_changed_at)})`
      : '';
    parts.push(`${reason.replace(/\.$/, '')}${when}.`);
  }
  if (guideline.edited_at) {
    parts.push(
      `Editado${guideline.edited_by ? ` por ${guideline.edited_by}` : ''} el ${formatDate(dayFormat, guideline.edited_at)}.`,
    );
  }
  return parts.length > 0 ? parts.join(' ') : null;
}

function EditGuidelineDialog({
  guideline,
  onClose,
}: {
  guideline: Guideline;
  onClose: () => void;
}) {
  const [form, setForm] = useState<GuidelineEditForm>(() => guidelineEditForm(guideline));
  const update = useUpdateGuideline();
  const ids = useId();
  const refusal = useRef<HTMLDivElement>(null);
  // The refusal sits under the last field: bring it into view, or a 422 after scrolling
  // through the fifteen kinds of email is a button that seems to do nothing.
  useEffect(() => {
    if (update.isError) refusal.current?.scrollIntoView?.({ block: 'nearest' });
  }, [update.isError]);
  const change = guidelineChanges(guideline, form);
  const valid = form.text.trim() !== '';
  const formId = `editar-criterio-${guideline.id}`;

  function set<K extends keyof GuidelineEditForm>(key: K, value: GuidelineEditForm[K]) {
    setForm((current) => ({ ...current, [key]: value }));
    update.reset();
  }

  function toggle(category: EmailCategory, checked: boolean) {
    const next = checked
      ? [...form.appliesTo, category]
      : form.appliesTo.filter((each) => each !== category);
    set('appliesTo', inOrder(next));
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    update.mutate({ id: guideline.id, change }, { onSuccess: onClose });
  }

  return (
    <Dialog
      title="Editar criterio"
      description={`Nota de ${guideline.author ?? 'alguien'}: «${guideline.note}»`}
      size="lg"
      onClose={update.isPending ? () => undefined : onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={update.isPending}>
            Cancelar
          </Button>
          <Button
            type="submit"
            form={formId}
            disabled={update.isPending || !valid || Object.keys(change).length === 0}
          >
            {update.isPending ? 'Guardando…' : 'Guardar cambios'}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        onSubmit={submit}
        aria-label="Editar criterio"
        className="flex flex-col gap-4"
      >
        <div className="flex flex-col gap-1">
          <label htmlFor={`${ids}-texto`} className="text-ink-2 text-[13.5px] font-medium">
            Qué dice
          </label>
          <textarea
            id={`${ids}-texto`}
            className="field h-auto min-h-20 py-2"
            rows={3}
            maxLength={500}
            value={form.text}
            onChange={(event) => set('text', event.target.value)}
            required
          />
        </div>
        <Select
          label="Alcance"
          value={form.scope}
          onChange={(event) => set('scope', event.target.value as GuidelineEditForm['scope'])}
          hint="«Solo ese correo» no cambia nada de los siguientes."
        >
          {SCOPES.map((scope) => (
            <option key={scope} value={scope}>
              {SCOPE_LABELS[scope]}
            </option>
          ))}
        </Select>
        {scopeHasCondition(form.scope) ? (
          <Input
            label="Condición"
            value={form.condition}
            maxLength={500}
            hint="Cuándo vale. El sistema mira si el correo la cumple. Vacía, vale lo que dice el criterio."
            onChange={(event) => set('condition', event.target.value)}
          />
        ) : null}
        <fieldset className="flex flex-col gap-2">
          <legend className="text-ink-2 text-[13.5px] font-medium">Tipo de correo</legend>
          <p className="text-ink-3 text-[12.5px]">Sin marcar ninguno, vale para cualquiera.</p>
          <div className="grid gap-1.5 sm:grid-cols-2">
            {CATEGORIES.map((category) => (
              <label key={category} className="text-ink-2 flex items-center gap-2 text-[13px]">
                <input
                  type="checkbox"
                  className="accent-accent size-4"
                  checked={form.appliesTo.includes(category)}
                  onChange={(event) => toggle(category, event.target.checked)}
                />
                {CATEGORY_LABELS[category]}
              </label>
            ))}
          </div>
        </fieldset>
        <Select
          label="Qué es (y su carpeta)"
          value={form.category}
          onChange={(event) => set('category', event.target.value as EmailCategory | '')}
        >
          <option value="">No lo cambia</option>
          {CATEGORIES.map((category) => (
            <option key={category} value={category}>
              {CATEGORY_LABELS[category]}
            </option>
          ))}
        </Select>
        <Select
          label="A quién va"
          value={form.destination}
          onChange={(event) => set('destination', event.target.value as TriageDestination | '')}
        >
          <option value="">No lo cambia</option>
          {DESTINATIONS.map((destination) => (
            <option key={destination} value={destination}>
              {DESTINATION_LABELS[destination]}
            </option>
          ))}
        </Select>
        <div ref={refusal}>
          {update.isError ? (
            <ErrorMessage error={update.error}>No se pudo guardar</ErrorMessage>
          ) : null}
        </div>
      </form>
    </Dialog>
  );
}
